import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonComponent } from '../button/button.component';
import { IconComponent } from '../../../icons/icon.component';
import type { NavIconName } from '../../../icons/nav-icon.model';

/**
 * Interface para ações de erro
 */
export interface ErrorAction {
  label: string;
  handler: () => void;
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  primary?: boolean;
}

/**
 * Interface para mensagem de erro estruturada
 */
export interface ErrorMessage {
  title: string;
  message: string;
  actions?: ErrorAction[];
  severity?: 'error' | 'warning' | 'info';
  details?: string;
}

/**
 * MG3P Error State Component
 * 
 * Componente para exibir estados de erro com mensagens claras,
 * ações corretivas e detalhes técnicos opcionais.
 * 
 * @example
 * ```html
 * <mg3p-error-state
 *   title="Não foi possível carregar os dados"
 *   message="Verifique sua conexão com a internet e tente novamente."
 *   severity="error"
 *   [actions]="errorActions"
 *   details="Error 500: Internal Server Error"
 * ></mg3p-error-state>
 * ```
 */
@Component({
  selector: 'mg3p-error-state',
  standalone: true,
  imports: [CommonModule, ButtonComponent, IconComponent],
  template: `
    <div class="mg3p-error" [class]="'mg3p-error--' + severity" role="alert">
      <div class="mg3p-error__icon" aria-hidden="true">
        <mg3p-icon [name]="resolvedIconName()" size="lg" />
      </div>
      
      <h3 class="mg3p-error__title">{{ title }}</h3>
      
      <p class="mg3p-error__message">{{ message }}</p>
      
      @if (actions && actions.length > 0) {
        <div class="mg3p-error__actions">
          @for (action of actions; track action.label) {
            <mg3p-button
              [variant]="action.variant || (action.primary ? 'primary' : 'secondary')"
              size="md"
              (clicked)="action.handler()"
            >
              {{ action.label }}
            </mg3p-button>
          }
        </div>
      }
      
      @if (details) {
        <details class="mg3p-error__details">
          <summary class="mg3p-error__details-summary">
            Detalhes técnicos
          </summary>
          <pre class="mg3p-error__details-content">{{ details }}</pre>
        </details>
      }
      
      <ng-content></ng-content>
    </div>
  `,
  styles: [`
    .mg3p-error {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      padding: var(--space-xl);
      border-radius: var(--radius-lg);
      border: 1px solid;
    }

    /* ========================================
       SEVERITY VARIANTS
       ======================================== */
    
    .mg3p-error--error {
      background: linear-gradient(
        180deg,
        color-mix(in srgb, var(--color-danger-50) 80%, transparent) 0%,
        var(--color-bg-primary) 100%
      );
      border-color: var(--color-danger-500);
    }

    .mg3p-error--warning {
      background: linear-gradient(
        180deg,
        color-mix(in srgb, var(--color-warning-50) 80%, transparent) 0%,
        var(--color-bg-primary) 100%
      );
      border-color: var(--color-warning-500);
    }

    .mg3p-error--info {
      background: linear-gradient(
        180deg,
        color-mix(in srgb, var(--color-info-50) 80%, transparent) 0%,
        var(--color-bg-primary) 100%
      );
      border-color: var(--color-info-500);
    }

    /* ========================================
       ICON
       ======================================== */
    
    .mg3p-error__icon {
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: var(--space-md);
    }

    .mg3p-error__icon .mg3p-icon--lg {
      width: 3rem;
      height: 3rem;
    }

    .mg3p-error--error .mg3p-error__icon {
      color: var(--color-danger-500);
    }

    .mg3p-error--warning .mg3p-error__icon {
      color: var(--color-warning-700);
    }

    .mg3p-error--info .mg3p-error__icon {
      color: var(--color-info-500);
    }

    /* ========================================
       TEXT
       ======================================== */
    
    .mg3p-error__title {
      margin: 0 0 var(--space-sm);
      font-size: var(--font-size-xl);
      font-weight: var(--font-weight-semibold);
      color: var(--color-text-primary);
      max-width: 560px;
    }

    .mg3p-error__message {
      margin: 0 0 var(--space-lg);
      font-size: var(--font-size-base);
      color: var(--color-text-secondary);
      line-height: var(--line-height-relaxed);
      max-width: 640px;
    }

    /* ========================================
       ACTIONS
       ======================================== */
    
    .mg3p-error__actions {
      display: flex;
      flex-wrap: wrap;
      gap: var(--space-sm);
      justify-content: center;
      margin-bottom: var(--space-md);
    }

    /* ========================================
       DETAILS
       ======================================== */
    
    .mg3p-error__details {
      width: 100%;
      max-width: 640px;
      margin-top: var(--space-lg);
      padding: var(--space-md);
      background: var(--color-bg-elevated);
      border: 1px solid var(--color-border-light);
      border-radius: var(--radius-md);
      text-align: left;
    }

    .mg3p-error__details-summary {
      cursor: pointer;
      font-size: var(--font-size-sm);
      font-weight: var(--font-weight-semibold);
      color: var(--color-text-secondary);
      user-select: none;
      list-style: none;
      display: flex;
      align-items: center;
      gap: var(--space-xs);
    }

    .mg3p-error__details-summary::-webkit-details-marker {
      display: none;
    }

    .mg3p-error__details-summary::before {
      content: '▶';
      display: inline-block;
      transition: transform var(--transition-fast);
    }

    .mg3p-error__details[open] .mg3p-error__details-summary::before {
      transform: rotate(90deg);
    }

    .mg3p-error__details-summary:hover {
      color: var(--color-text-primary);
    }

    .mg3p-error__details-summary:focus-visible {
      outline: 2px solid var(--color-border-focus);
      outline-offset: 2px;
      border-radius: var(--radius-sm);
    }

    .mg3p-error__details-content {
      margin: var(--space-md) 0 0;
      padding: var(--space-sm);
      background: var(--color-neutral-900);
      color: var(--color-neutral-100);
      font-family: var(--font-family-mono);
      font-size: var(--font-size-xs);
      line-height: var(--line-height-relaxed);
      border-radius: var(--radius-sm);
      overflow-x: auto;
      white-space: pre-wrap;
      word-break: break-word;
    }
  `]
})
export class ErrorStateComponent {
  /**
   * Título do erro
   */
  @Input() title = 'Ocorreu um erro';

  /**
   * Mensagem descritiva do erro
   */
  @Input() message = 'Não foi possível completar a operação.';

  /**
   * Severidade do erro
   */
  @Input() severity: 'error' | 'warning' | 'info' = 'error';

  /**
   * Ações disponíveis para o usuário
   */
  @Input() actions?: ErrorAction[];

  /**
   * Detalhes técnicos (opcional, colapsável)
   */
  @Input() details?: string;

  /**
   * Ícone SVG (opcional; padrão conforme severidade)
   */
  @Input() iconName?: NavIconName;

  resolvedIconName(): NavIconName {
    if (this.iconName) {
      return this.iconName;
    }
    switch (this.severity) {
      case 'warning':
        return 'bolt';
      case 'info':
        return 'info';
      default:
        return 'alert-triangle';
    }
  }
}

/**
 * Helper function para criar mensagens de erro estruturadas
 */
export function createErrorMessage(
  error: any,
  defaultMessage = 'Ocorreu um erro inesperado'
): ErrorMessage {
  // Erro de rede (sem conexão)
  if (error.status === 0) {
    return {
      title: 'Sem conexão',
      message: 'Não foi possível conectar ao servidor. Verifique sua conexão com a internet.',
      severity: 'warning',
      actions: [
        {
          label: 'Tentar novamente',
          handler: () => window.location.reload(),
          primary: true,
        },
      ],
    };
  }

  // Erro de autenticação
  if (error.status === 401) {
    return {
      title: 'Sessão expirada',
      message: 'Sua sessão expirou. Por favor, faça login novamente.',
      severity: 'warning',
      actions: [
        {
          label: 'Fazer login',
          handler: () => window.location.href = '/login',
          primary: true,
        },
      ],
    };
  }

  // Erro de permissão
  if (error.status === 403) {
    return {
      title: 'Acesso negado',
      message: 'Você não tem permissão para realizar esta operação. Entre em contato com o administrador.',
      severity: 'error',
      actions: [
        {
          label: 'Voltar',
          handler: () => window.history.back(),
          primary: true,
        },
      ],
    };
  }

  // Recurso não encontrado
  if (error.status === 404) {
    return {
      title: 'Não encontrado',
      message: 'O recurso solicitado não foi encontrado.',
      severity: 'warning',
      actions: [
        {
          label: 'Voltar',
          handler: () => window.history.back(),
          primary: true,
        },
      ],
    };
  }

  // Erro de validação
  if (error.status === 422) {
    return {
      title: 'Dados inválidos',
      message: error.error?.detail || 'Os dados fornecidos são inválidos. Verifique e tente novamente.',
      severity: 'warning',
      details: JSON.stringify(error.error, null, 2),
    };
  }

  // Erro do servidor
  if (error.status >= 500) {
    return {
      title: 'Erro no servidor',
      message: 'Ocorreu um erro no servidor. Nossa equipe foi notificada. Tente novamente mais tarde.',
      severity: 'error',
      actions: [
        {
          label: 'Tentar novamente',
          handler: () => window.location.reload(),
          primary: true,
        },
      ],
      details: error.error?.detail || error.message,
    };
  }

  // Erro genérico
  return {
    title: 'Erro',
    message: error.error?.detail || error.message || defaultMessage,
    severity: 'error',
    details: JSON.stringify(error, null, 2),
  };
}

// Made with Bob
